import React, { useEffect, useState } from "react";
import { resolveRoleName } from "../utils/auth";
import { buildAdminTreeUserIds } from "../utils/userHierarchy";
import {
  importDashboardExcel,
  getDashboardCounts,
  getDashboardCountsByUsers,
} from "../services/dashboardService";
import { useAppContext } from "../context/AppContext";
import { toast } from "../utils/toast";
import "../styles/dashboard-page.css";
import ExcelImportModal from "../components/ui/ExcelImportModal";
import ExportCompanyModal from "../components/ui/ExportCompanyModal";
import WorldMap from "../components/WorldMap";

const DashboardPage = () => {
  const { setActiveSection } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    companies: 0,
    processes: 0,
    tasks: 0,
    jobs: 0,
  });
  const [importOpen, setImportOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        let user = null;
        try {
          const raw =
            sessionStorage.getItem("auth") || localStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}

        if (user) {
          const rn = await resolveRoleName(user);
          const norm = String(rn || "")
            .toUpperCase()
            .replace(/[^A-Z]/g, "");

          if (norm === "SUPERADMIN") {
            // SUPERADMIN: Use GET /dashboard for total system counts
            const counts = await getDashboardCounts();
            if (!mounted) return;
            setStats(counts);
          } else {
            // ADMIN or Normal User: Use POST /hierarchical-view/relation/dashboard/bulk
            const selfId = Number(user.user_id ?? user.id);
            const ids =
              norm === "ADMIN" ? await buildAdminTreeUserIds(selfId)
              : Number.isInteger(selfId) ? [selfId]
              : [];

            if (ids.length > 0) {
              const counts = await getDashboardCountsByUsers(ids);
              if (!mounted) return;
              setStats(counts);
            } else {
              if (!mounted) return;
              setStats({ companies: 0, processes: 0, tasks: 0, jobs: 0 });
            }
          }
        } else {
          // No user logged in - try to get system counts
          try {
            const counts = await getDashboardCounts();
            if (!mounted) return;
            setStats(counts);
          } catch {
            if (!mounted) return;
            setStats({ companies: 0, processes: 0, tasks: 0, jobs: 0 });
          }
        }
      } catch (e) {
        if (mounted) {
          toast.error("Failed to load dashboard data");
          setStats({ companies: 0, processes: 0, tasks: 0, jobs: 0 });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="dashboard-content-area">
      <div className="dashboard-header-section">
        <div className="dashboard-title-row">
          <h2 className="dashboard-title">Dashboard Overview</h2>
          <div className="dashboard-actions">
            <button
              type="button"
              className="btn btn-import"
              onClick={() => {
                setImportError(null);
                setImportOpen(true);
              }}
            >
              Import Data
            </button>
            <button
              type="button"
              className="btn btn-export"
              onClick={() => setExportOpen(true)}
            >
              Export Data
            </button>
          </div>
        </div>
        <p className="dashboard-subtitle">Quick overview of key CMS metrics</p>
      </div>

      <ExcelImportModal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          setImportError(null);
          setImportResult(null);
        }}
        uploading={uploading}
        error={importError}
        result={importResult}
        onClearResult={() => setImportResult(null)}
        onFileSelected={async (file) => {
          setUploading(true);
          setImportError(null);
          try {
            const res = await importDashboardExcel(file);
            setImportResult(res);
            // Optional: reload stats after successful import
            // You can trigger a refetch here if backend affects these counts immediately.
          } catch (e) {
            setImportError(e?.message || "Upload failed");
          } finally {
            setUploading(false);
          }
        }}
      />

      <ExportCompanyModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
      />

      {/* System Stats Widgets (real data) */}
      {loading && (
        <div style={{ padding: "0.75rem", color: "#64748b" }}>
          Loading dashboard…
        </div>
      )}
      {!loading && (
        <div className="stats-grid">
          <div
            className="stat-card"
            onClick={() => setActiveSection("company-management")}
            style={{ cursor: "pointer" }}
          >
            <div className="stat-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M22 19.07l-7.07-7.07a6 6 0 11-2.83-2.83L19.07 16.24 22 19.07zM7 13a4 4 0 100-8 4 4 0 000 8z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="stat-content">
              <h3 className="stat-number">{stats.companies}</h3>
              <p className="stat-label">Companies</p>
            </div>
          </div>

          <div
            className="stat-card"
            onClick={() => setActiveSection("process-management")}
            style={{ cursor: "pointer" }}
          >
            <div className="stat-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M4 7h16v4H4V7zm0 6h10v4H4v-4z" fill="currentColor" />
              </svg>
            </div>
            <div className="stat-content">
              <h3 className="stat-number">{stats.processes}</h3>
              <p className="stat-label">Processes</p>
            </div>
          </div>

          <div
            className="stat-card"
            onClick={() => setActiveSection("task-management")}
            style={{ cursor: "pointer" }}
          >
            <div className="stat-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 11l3 3L22 4l-2-2-8 8-3-3-2 2 5 5zM2 20h20v2H2v-2z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="stat-content">
              <h3 className="stat-number">{stats.tasks}</h3>
              <p className="stat-label">Tasks</p>
            </div>
          </div>

          <div
            className="stat-card"
            onClick={() => setActiveSection("job-management")}
            style={{ cursor: "pointer" }}
          >
            <div className="stat-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19.14 12.94a7.992 7.992 0 000-1.88l2.03-1.58a.5.5 0 00.12-.64l-1.92-3.32a.5.5 0 00-.6-.22l-2.39.96a7.963 7.963 0 00-1.62-.94l-.36-2.54A.5.5 0 0013.9 0h-3.8a.5.5 0 00-.5.42l-.36 2.54c-.57.22-1.11.51-1.62.94l-2.39-.96a.5.5 0 00-.6.22L1.71 6.04a.5.5 0 00.12.64l2.03 1.58c-.05.62-.05 1.25 0 1.88L1.83 11.7a.5.5 0 00-.12.64l1.92 3.32c.13.22.39.31.6.22l2.39-.96c.5.43 1.05.77 1.62.99l.36 2.54c.04.24.25.42.5.42h3.8c.25 0 .46-.18.5-.42l.36-2.54c.57-.22 1.11-.55 1.62-.99l2.39.96c.22.09.47 0 .6-.22l1.92-3.32a.5.5 0 00-.12-.64l-2.03-1.58zM12 15.5a3.5 3.5 0 110-7 3.5 3.5 0 010 7z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="stat-content">
              <h3 className="stat-number">{stats.jobs}</h3>
              <p className="stat-label">Jobs</p>
            </div>
          </div>
        </div>
      )}

      <WorldMap />
    </div>
  );
};

export default DashboardPage;
